import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Bell, BellOff, X, Share } from "lucide-react";
import api from "@/lib/api";
import { pushSupported, isIOS, isStandalone, currentSubscription, enablePush, disablePush } from "@/lib/push";

const yellow = "bg-[#E3FF00] text-neutral-900 font-semibold rounded-lg hover:brightness-95 transition-[filter] inline-flex items-center gap-2";

const useInstallPrompt = () => {
  const [evt, setEvt] = useState(window.__mppInstallEvt || null);
  useEffect(() => {
    const h = () => setEvt(window.__mppInstallEvt || null);
    window.addEventListener("mpp-install-change", h);
    return () => window.removeEventListener("mpp-install-change", h);
  }, []);
  return evt;
};

export const InstallAppButton = ({ className = "" }) => {
  const evt = useInstallPrompt();
  const [iosHelp, setIosHelp] = useState(false);
  if (isStandalone() || (!evt && !isIOS())) return null;
  const install = async () => {
    if (!evt) return setIosHelp(true);
    evt.prompt();
    await evt.userChoice;
    window.__mppInstallEvt = null;
    window.dispatchEvent(new Event("mpp-install-change"));
  };
  return (
    <>
      <button onClick={install} className={`${yellow} px-3 py-2 text-sm ${className}`} data-testid="install-app-button"><Download className="h-4 w-4" /> Installa app</button>
      {iosHelp && <IosHelp onClose={() => setIosHelp(false)} />}
    </>
  );
};

const IosHelp = ({ onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-3 text-sm text-neutral-800" onClick={(e) => e.stopPropagation()} data-testid="ios-install-help">
      <div className="font-display font-bold text-lg">Installa Mypasspartu su iPhone/iPad</div>
      <p>1. Apri il sito con <b>Safari</b>.</p>
      <p className="flex items-center gap-1">2. Tocca <Share className="h-4 w-4" /> <b>Condividi</b>.</p>
      <p>3. Scegli <b>Aggiungi alla schermata Home</b>.</p>
      <p>4. Apri l'app dall'icona MP e attiva le notifiche.</p>
      <button onClick={onClose} className={`${yellow} w-full justify-center py-2.5`} data-testid="ios-install-help-close">Ho capito</button>
    </div>
  </div>
);

const usePushState = () => {
  const [on, setOn] = useState(null);
  useEffect(() => { currentSubscription().then((s) => setOn(!!s && Notification.permission === "granted")).catch(() => setOn(false)); }, []);
  const enable = async () => {
    try { await enablePush(); setOn(true); toast.success("Notifiche attivate"); }
    catch { toast.error(Notification.permission === "denied" ? "Notifiche bloccate: abilitale dalle impostazioni del browser" : "Impossibile attivare le notifiche"); }
  };
  const disable = async () => { await disablePush(); setOn(false); toast.success("Notifiche disattivate"); };
  return { on, enable, disable };
};

export const NotificationBanner = () => {
  const { on, enable } = usePushState();
  const [hidden, setHidden] = useState(localStorage.getItem("mpp_push_banner") === "hidden");
  const iosNeedsInstall = isIOS() && !isStandalone();
  if (hidden || on !== false || (!pushSupported() && !iosNeedsInstall) || (typeof Notification !== "undefined" && Notification.permission === "denied")) return null;
  const dismiss = () => { localStorage.setItem("mpp_push_banner", "hidden"); setHidden(true); };
  return (
    <div className="mb-5 rounded-xl border border-neutral-900/10 bg-[#E3FF00]/25 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="push-banner">
      <Bell className="h-5 w-5 shrink-0 text-neutral-900" />
      <div className="flex-1 text-sm text-neutral-900">
        <b>Attiva le notifiche</b> per sapere subito di risposte ai ticket, servizi attivati e borsellino in esaurimento.
        {iosNeedsInstall && <span className="block text-xs mt-1">Su iPhone/iPad prima installa l'app: Condividi → Aggiungi alla schermata Home.</span>}
      </div>
      <div className="flex gap-2">
        {iosNeedsInstall ? <InstallAppButton /> : <button onClick={enable} className={`${yellow} px-4 py-2 text-sm`} data-testid="push-banner-enable">Attiva notifiche</button>}
        <button onClick={dismiss} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-black/5" aria-label="Chiudi" data-testid="push-banner-dismiss"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export const NotificationSettings = () => {
  const { on, enable, disable } = usePushState();
  const test = async () => { const { data } = await api.post("/push/test"); toast.success(data.sent ? "Notifica di prova inviata" : "Nessun dispositivo attivo"); };
  const supported = pushSupported();
  return (
    <div className="bg-card rounded-xl border border-border/80 p-5 sm:p-6 space-y-3" data-testid="notification-settings">
      <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Bell className="h-5 w-5 text-primary" /> Notifiche e app</h2>
      <p className="text-sm text-muted-foreground">Ricevi avvisi su questo dispositivo anche ad app chiusa. Installa l'app per aprirla dal desktop o dalla schermata Home.</p>
      {!supported && <p className="text-sm text-amber-700">{isIOS() ? "Su iPhone/iPad installa prima l'app (Condividi → Aggiungi alla schermata Home) e aprila dall'icona." : "Questo browser non supporta le notifiche."}</p>}
      <div className="flex flex-wrap gap-2">
        {supported && (on
          ? <><button onClick={disable} className={`${yellow} px-4 py-2 text-sm`} data-testid="push-disable-button"><BellOff className="h-4 w-4" /> Disattiva notifiche</button>
              <button onClick={test} className={`${yellow} px-4 py-2 text-sm`} data-testid="push-test-button">Invia notifica di prova</button></>
          : <button onClick={enable} className={`${yellow} px-4 py-2 text-sm`} data-testid="push-enable-button"><Bell className="h-4 w-4" /> Attiva notifiche</button>)}
        <InstallAppButton />
      </div>
    </div>
  );
};
